import type { ReloadFormRequest } from 'apiConnector/batch-workspace';
import { batchWorkspaceApiConnector } from 'apiConnector/batch-workspace';
import { lastQuoteApi } from 'apiConnector/quote/lastquote';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import type { BatchFormResult } from 'models/forms/BatchFormResult';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { forkJoin, of, timeout } from 'rxjs';
import { catchError, mergeMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Action } from 'state/actions';
import {
  BatchFormQuoteReloadRequested,
  batchPreviousQuoteLoaded,
  defaultBatchFormFailed,
  defaultBatchFormTimeouted,
} from 'state/batchForm/batchForm.action';
import { defaultBatchFormReceivedThunk } from 'state/batchForm/batchForm.thunks';
import { QuoteWithRegulation } from 'state/quotes/results/result.model';
import type { EpicWithThunk, Thunk } from 'state/state.common';
import { getTodayStringInLocalTimezone } from 'utils/core/date';

export interface BatchFormReloadApi {
  reloadForm(request: ReloadFormRequest): Observable<BatchFormResult>;
}

export function fetchBatchFormWhenQuoteReloadEpic(
  timeoutInMillis: number = 60000,
  appScheduler?: SchedulerLike,
): EpicWithThunk {
  return (actions$, state$, { httpClient, selectors: { isUserPreferencesPrivateMode } }) => {
    return actions$.pipe(
      ofType('BATCH_FORM_QUOTE_RELOAD_REQUESTED'),
      withLatestFrom(state$),
      mergeMap(([action, state]) => {
        const isPrivateMode = isUserPreferencesPrivateMode(state);
        const reloadFormRequest = buildReloadFormRequest(action, isPrivateMode);

        const tabRemoved$ = tabRemoved(actions$, action.tabId, state$);
        const reloadForm$ = batchWorkspaceApiConnector(httpClient).reloadForm(reloadFormRequest);
        const getLastQuote$ = lastQuoteApi(httpClient)
          .getLastQuote(action.quoteId)
          .pipe(catchError(_ => of(null)));

        return forkJoin([reloadForm$, getLastQuote$]).pipe(
          takeUntil(tabRemoved$),
          mergeMap(([form, quoteWithRegulation]) => {
            const actions = buildActions(action, form, quoteWithRegulation);
            return of(...actions);
          }),
          catchError(_ => of(defaultBatchFormFailed(action.temporaryFormId))),
          timeout({
            each: timeoutInMillis,
            with: () => of(defaultBatchFormTimeouted(action.temporaryFormId)),
            scheduler: appScheduler,
          }),
        );
      }),
    );
  };
}

function buildReloadFormRequest(action: BatchFormQuoteReloadRequested, isPrivateMode: boolean): ReloadFormRequest {
  return {
    id: action.id,
    idType: action.idType,
    tradeDate: getTodayStringInLocalTimezone(),
    productSubType: action.productSubType,
    processingChain: action.processingChain,
    pricerRfqId: action.pricerRfqId,
    isPrivateMode,
    productType: action.productType,
  };
}

function buildActions(
  action: BatchFormQuoteReloadRequested,
  form: BatchFormResult,
  quoteWithRegulation: QuoteWithRegulation | null,
) {
  const actions: (Action | Thunk<void>)[] = [
    defaultBatchFormReceivedThunk(
      action.tabId,
      action.temporaryFormId,
      form.negotiationId,
      form.productView,
      form.actions,
      form.values,
      form.fieldsMetadata,
      form.quoteQuery,
      form.version,
      form.mustForceAdvancedMode ?? false,
      form.allowedRequests,
      form.errors,
    ),
  ];

  if (quoteWithRegulation) {
    actions.push(batchPreviousQuoteLoaded(form.negotiationId, action.quoteId, quoteWithRegulation.quote));
  }

  return actions;
}
